/**
 * V37.32 — Simulateur live « avant / apres » pour le panel admin DOMIUM.
 *
 * Affiche 5 scenarios types et compare en TEMPS REEL :
 *   - AVANT : valeurs DE REFERENCE (config sauvegardee actuelle, ou
 *     valeurs par defaut si pas de config admin custom)
 *   - APRES : valeurs CALCULEES depuis le formulaire en cours d'edition
 *     (configForm) — la prevue avant Enregistrer.
 *
 * Permet de detecter immediatement :
 *   - Un coeff trop fort qui fait depasser le taux_max (clamping silencieux)
 *   - Un impact disproportionne sur certains scenarios
 *   - L'effet combine de plusieurs reglages
 */
import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { TrendingUp, TrendingDown, Minus, Sparkles, AlertTriangle } from 'lucide-react';
import { computeContribution } from './lib/domiumCalc';

const SCENARIOS = [
  {
    id: 'a', label: 'Petit projet individuel',
    cout_construction_ht: 100000, territoire: 'guadeloupe',
    type_operation: 'construction_neuve', type_batiment: 'individuel',
    mesures_prevention: false, part_entreprises_ess: 0,
  },
  {
    id: 'b', label: 'Projet moyen ESS',
    cout_construction_ht: 350000, territoire: 'martinique',
    type_operation: 'construction_neuve', type_batiment: 'collectif',
    mesures_prevention: true, part_entreprises_ess: 100,
  },
  {
    id: 'c', label: 'Rehabilitation grand',
    cout_construction_ht: 800000, territoire: 'guyane',
    type_operation: 'rehabilitation', type_batiment: 'collectif',
    mesures_prevention: false, part_entreprises_ess: 0,
  },
  {
    id: 'd', label: 'Tertiaire premium',
    cout_construction_ht: 1500000, territoire: 'reunion',
    type_operation: 'extension', type_batiment: 'tertiaire',
    mesures_prevention: true, part_entreprises_ess: 50,
  },
  {
    id: 'e', label: 'Tres grand projet',
    cout_construction_ht: 3000000, territoire: 'mayotte',
    type_operation: 'construction_neuve', type_batiment: 'mixte',
    mesures_prevention: true, part_entreprises_ess: 0,
  },
];

const fmtEUR = (n) => new Intl.NumberFormat('fr-FR', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
}).format(n || 0);

const fmtPct = (n) => `${(n || 0).toFixed(3).replace(/\.?0+$/, '')}%`;

function DeltaBadge({ before, after }) {
  if (!before && !after) return null;
  const diff = after - before;
  const pct = before > 0 ? (diff / before) * 100 : 0;
  if (Math.abs(diff) < 0.5) {
    return (
      <Badge variant="outline" className="bg-slate-50 text-slate-600 border-slate-300 text-[10px] font-semibold" data-testid="delta-equal">
        <Minus className="h-3 w-3 mr-1" /> identique
      </Badge>
    );
  }
  if (diff > 0) {
    return (
      <Badge className="bg-rose-700 text-white border-rose-900 text-[10px] font-semibold" data-testid="delta-up">
        <TrendingUp className="h-3 w-3 mr-1" /> +{pct.toFixed(1)}% ({fmtEUR(diff)})
      </Badge>
    );
  }
  return (
    <Badge className="bg-emerald-700 text-white border-emerald-900 text-[10px] font-semibold" data-testid="delta-down">
      <TrendingDown className="h-3 w-3 mr-1" /> {pct.toFixed(1)}% ({fmtEUR(diff)})
    </Badge>
  );
}

export default function LiveSimulator({ configBefore, configAfter }) {
  const [showDetails, setShowDetails] = useState(false);
  const rows = useMemo(() => SCENARIOS.map((s) => {
    const before = computeContribution(s, configBefore);
    const after = computeContribution(s, configAfter);
    return { scenario: s, before, after };
  }), [configBefore, configAfter]);

  const anyClamped = rows.some(r => r.after.was_clamped);

  return (
    <Card className="border-amber-200 bg-gradient-to-br from-amber-50 via-white to-orange-50 mt-4" data-testid="live-simulator">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-amber-600" />
            <h4 className="font-bold text-amber-900 text-sm uppercase tracking-wide">
              Simulateur live « avant / après »
            </h4>
          </div>
          <div className="flex items-center gap-2">
            {anyClamped && (
              <Badge className="bg-rose-600 text-white font-semibold" data-testid="simulator-clamp-warning">
                <AlertTriangle className="h-3 w-3 mr-1" />
                Taux écrêté par taux_max
              </Badge>
            )}
            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="text-xs text-amber-800 underline hover:no-underline font-medium"
              data-testid="simulator-toggle-details"
            >
              {showDetails ? 'Masquer le détail' : 'Voir le détail'}
            </button>
          </div>
        </div>
        <p className="text-[11px] text-amber-800/80">
          5 scénarios types calculés en direct selon vos modifications.
          <strong> AVANT</strong> = configuration sauvegardée actuelle.
          <strong> APRÈS</strong> = configuration en cours d&apos;édition (non encore enregistrée).
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="simulator-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-amber-900 border-b border-amber-200">
                <th className="text-left py-1.5 px-2">Scénario</th>
                <th className="text-right py-1.5 px-2">Avant</th>
                <th className="text-right py-1.5 px-2">Après</th>
                <th className="text-right py-1.5 px-2">Variation</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ scenario, before, after }) => (
                <React.Fragment key={scenario.id}>
                  <tr className="border-b border-amber-100 hover:bg-amber-50" data-testid={`simulator-row-${scenario.id}`}>
                    <td className="py-2 px-2">
                      <div className="font-semibold text-slate-800">{scenario.label}</div>
                      <div className="text-[10px] text-slate-500">
                        {fmtEUR(scenario.cout_construction_ht)} · {scenario.territoire} · {scenario.type_operation} · {scenario.type_batiment}
                        {scenario.mesures_prevention && ' · prév.'}
                        {scenario.part_entreprises_ess > 0 && ` · ESS ${scenario.part_entreprises_ess}%`}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-right">
                      <div className="font-mono font-semibold text-slate-700" data-testid={`simulator-before-${scenario.id}`}>
                        {fmtEUR(before.contribution_ht)}
                      </div>
                      <div className="text-[10px] text-slate-400">{fmtPct(before.taux_final_pct)}</div>
                    </td>
                    <td className="py-2 px-2 text-right">
                      <div className={`font-mono font-bold ${after.was_clamped ? 'text-rose-700' : 'text-amber-800'}`} data-testid={`simulator-after-${scenario.id}`}>
                        {fmtEUR(after.contribution_ht)}
                      </div>
                      <div className="text-[10px] text-slate-500">
                        {fmtPct(after.taux_final_pct)}
                        {after.was_clamped && <span className="text-rose-600 ml-1">⚠ clamp</span>}
                      </div>
                    </td>
                    <td className="py-2 px-2 text-right">
                      <DeltaBadge before={before.contribution_ht} after={after.contribution_ht} />
                    </td>
                  </tr>
                  {showDetails && (
                    <tr className="bg-amber-50/40">
                      <td colSpan={4} className="py-1.5 px-2 text-[10px] text-slate-600 font-mono">
                        <span className="opacity-60">Avant</span> : tx_base={fmtPct(before.taux_base_pct)} ×c_terr {before.coeff_territoire} ×c_op {before.coeff_operation} ×c_bat {before.coeff_batiment} = {fmtPct(before.taux_effectif_pct)} − red {fmtPct(before.reduction_totale_pct)} ⇒ {fmtPct(before.taux_final_pct)}
                        <br />
                        <span className="opacity-60">Après</span> : tx_base={fmtPct(after.taux_base_pct)} ×c_terr {after.coeff_territoire} ×c_op {after.coeff_operation} ×c_bat {after.coeff_batiment} = {fmtPct(after.taux_effectif_pct)} − red {fmtPct(after.reduction_totale_pct)} ⇒ {fmtPct(after.taux_final_pct)}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
